import UIComponent from "../ui/component.js";
import MeetingsApi from "../api/meetings";

const TELEMOST_HOSTS = [
    'telemost.yandex.ru',
    'telemost.yandex.com',
    'telemost.360.yandex.ru',
];

const LOCALE_MAP = {
    ua: 'uk',
    no: 'nb',
};

export class MeetingTelemostForm extends UIComponent {
    init() {
        this.mode = 'now';
        this.viewDate = this.startOfMonth(new Date());
        this.selectedDate = null;

        this.invalidLinkLabel = this.element.dataset.invalidLink || '';
        this.missingDatetimeLabel = this.element.dataset.missingDatetime || '';
        this.pastDatetimeLabel = this.element.dataset.pastDatetime || '';
        this.scheduledLabel = this.element.dataset.scheduledLabel || '';
        this.saveErrorLabel = this.element.dataset.saveError || '';
        this.nowActionLabel = this.element.dataset.nowAction || '';
        this.scheduleActionLabel = this.element.dataset.scheduleAction || '';

        this.linkInput = this.element.querySelector('[data-tbx-name="telemostLink"]');
        this.schedulePanel = this.element.querySelector('[data-tbx-name="schedulePanel"]');
        this.calendarTitle = this.element.querySelector('[data-tbx-name="calendarTitle"]');
        this.weekdaysEl = this.element.querySelector('[data-tbx-name="calendarWeekdays"]');
        this.gridEl = this.element.querySelector('[data-tbx-name="calendarGrid"]');
        this.timeInput = this.element.querySelector('[data-tbx-name="telemostTime"]');
        this.errorEl = this.element.querySelector('[data-tbx-name="telemostError"]');
        this.successEl = this.element.querySelector('[data-tbx-name="telemostSuccess"]');
        this.submitBtn = this.element.querySelector('[data-tbx-name="telemostSubmitBtn"]');
        this.modeButtons = [...this.element.querySelectorAll('[data-telemost-mode]')];

        this.locale = this.resolveLocale();
        this.renderWeekdays();
        this.renderCalendar();
        this.setDefaultTime();

        this.modeButtons.forEach((button) => {
            button.addEventListener('click', () => this.setMode(button.dataset.telemostMode));
        });
        this.element.querySelector('[data-tbx-name="calendarPrev"]')?.addEventListener('click', () => this.shiftMonth(-1));
        this.element.querySelector('[data-tbx-name="calendarNext"]')?.addEventListener('click', () => this.shiftMonth(1));
        this.submitBtn?.addEventListener('click', () => this.submit());
        this.linkInput?.addEventListener('input', () => this.clearMessages());
    }

    resolveLocale() {
        const raw = document.documentElement.lang || 'ru';
        return LOCALE_MAP[raw] || raw;
    }

    setMode(mode) {
        this.mode = mode;
        this.clearMessages();

        this.modeButtons.forEach((button) => {
            const isActive = button.dataset.telemostMode === mode;
            button.classList.toggle('is-active', isActive);
            button.setAttribute('aria-checked', isActive ? 'true' : 'false');
        });

        const isSchedule = mode === 'schedule';
        this.schedulePanel?.classList.toggle('d-none', !isSchedule);
        this.schedulePanel?.toggleAttribute('hidden', !isSchedule);

        if (this.submitBtn) {
            this.submitBtn.textContent = isSchedule
                ? this.scheduleActionLabel
                : this.nowActionLabel;
        }

        if (isSchedule && !this.selectedDate) {
            this.selectDate(this.nextAvailableDate());
        }
    }

    shiftMonth(offset) {
        this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + offset, 1);
        this.renderCalendar();
    }

    renderWeekdays() {
        if (!this.weekdaysEl) {
            return;
        }

        const monday = new Date(Date.UTC(2023, 0, 2));
        const formatter = new Intl.DateTimeFormat(this.locale, { weekday: 'short' });

        this.weekdaysEl.innerHTML = Array.from({ length: 7 }, (_, index) => {
            const day = new Date(monday);
            day.setUTCDate(monday.getUTCDate() + index);
            return `<span>${this.escapeHtml(formatter.format(day))}</span>`;
        }).join('');
    }

    renderCalendar() {
        if (!this.gridEl || !this.calendarTitle) {
            return;
        }

        const titleFormatter = new Intl.DateTimeFormat(this.locale, { month: 'long', year: 'numeric' });
        this.calendarTitle.textContent = titleFormatter.format(this.viewDate);

        const year = this.viewDate.getFullYear();
        const month = this.viewDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const mondayBasedOffset = (firstDay.getDay() + 6) % 7;
        const today = this.startOfDay(new Date());

        const cells = [];
        for (let index = 0; index < mondayBasedOffset; index += 1) {
            cells.push('<span class="meeting-calendar__day is-empty"></span>');
        }

        for (let day = 1; day <= daysInMonth; day += 1) {
            const date = new Date(year, month, day);
            const isPast = date < today;
            const isToday = date.getTime() === today.getTime();
            const isSelected = this.selectedDate && this.startOfDay(this.selectedDate).getTime() === date.getTime();
            const classes = [
                'meeting-calendar__day',
                isPast ? 'is-disabled' : '',
                isToday ? 'is-today' : '',
                isSelected ? 'is-selected' : '',
            ].filter(Boolean).join(' ');

            cells.push(
                `<button type="button" class="${classes}" data-day="${day}" ${isPast ? 'disabled' : ''} aria-pressed="${isSelected ? 'true' : 'false'}">${day}</button>`
            );
        }

        this.gridEl.innerHTML = cells.join('');
        this.gridEl.querySelectorAll('[data-day]').forEach((button) => {
            button.addEventListener('click', () => {
                this.selectDate(new Date(year, month, Number(button.dataset.day)));
            });
        });
    }

    selectDate(date) {
        this.selectedDate = this.startOfDay(date);
        this.viewDate = this.startOfMonth(date);
        this.renderCalendar();
        this.clearMessages();
    }

    setDefaultTime() {
        if (!this.timeInput || this.timeInput.value) {
            return;
        }

        const now = new Date();
        now.setMinutes(now.getMinutes() + 15);
        now.setSeconds(0, 0);
        const rounded = Math.ceil(now.getMinutes() / 5) * 5;
        now.setMinutes(rounded);
        this.timeInput.value = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    }

    async submit() {
        this.clearMessages();

        if (!this.isValidTelemostLink(this.linkInput?.value || '')) {
            this.showError(this.invalidLinkLabel);
            this.linkInput?.focus();
            return;
        }

        if (this.mode === 'now') {
            return;
        }

        const scheduledAt = this.getScheduledDate();
        if (!scheduledAt) {
            this.showError(this.missingDatetimeLabel);
            return;
        }

        if (scheduledAt.getTime() <= Date.now()) {
            this.showError(this.pastDatetimeLabel);
            return;
        }

        if (this.submitBtn) {
            this.submitBtn.disabled = true;
        }

        try {
            const recording = await MeetingsApi.store({
                source: 'telemost',
                conference_url: this.linkInput.value.trim(),
                scheduled_at: scheduledAt.toISOString(),
            });

            if (recording?.errors) {
                this.showError(this.firstError(recording.errors));
                return;
            }

            document.dispatchEvent(new CustomEvent('meeting-scheduled', { detail: recording }));

            const formatted = new Intl.DateTimeFormat(this.locale, {
                dateStyle: 'long',
                timeStyle: 'short',
            }).format(scheduledAt);

            this.showSuccess(this.scheduledLabel.replace(':datetime', formatted));
        } catch (errors) {
            this.showError(this.firstError(errors));
        } finally {
            if (this.submitBtn) {
                this.submitBtn.disabled = false;
            }
        }
    }

    firstError(errors) {
        if (typeof errors === 'string' && errors.trim()) {
            return errors;
        }

        if (Array.isArray(errors) && errors.length) {
            return this.firstError(errors[0]);
        }

        if (errors && typeof errors === 'object') {
            const first = Object.values(errors)[0];
            if (first !== undefined) {
                return this.firstError(first);
            }
        }

        return this.saveErrorLabel;
    }

    getScheduledDate() {
        if (!this.selectedDate || !this.timeInput?.value) {
            return null;
        }

        const [hours, minutes] = this.timeInput.value.split(':').map((part) => Number(part));
        if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
            return null;
        }

        const date = new Date(this.selectedDate);
        date.setHours(hours, minutes, 0, 0);
        return date;
    }

    isValidTelemostLink(value) {
        try {
            const url = new URL(value.trim());
            if (url.protocol !== 'https:') {
                return false;
            }

            const host = url.hostname.replace(/^www\./, '');
            return TELEMOST_HOSTS.some((allowed) => host === allowed || host.endsWith(`.${allowed}`));
        } catch (error) {
            return false;
        }
    }

    nextAvailableDate() {
        return this.startOfDay(new Date());
    }

    startOfDay(date) {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    startOfMonth(date) {
        return new Date(date.getFullYear(), date.getMonth(), 1);
    }

    showError(message) {
        if (!this.errorEl) {
            return;
        }

        this.errorEl.textContent = message;
        this.errorEl.classList.remove('d-none');
        this.successEl?.classList.add('d-none');
    }

    showSuccess(message) {
        if (!this.successEl) {
            return;
        }

        this.successEl.textContent = message;
        this.successEl.classList.remove('d-none');
        this.errorEl?.classList.add('d-none');
    }

    clearMessages() {
        this.errorEl?.classList.add('d-none');
        this.successEl?.classList.add('d-none');
        if (this.errorEl) {
            this.errorEl.textContent = '';
        }
        if (this.successEl) {
            this.successEl.textContent = '';
        }
    }

    escapeHtml(value) {
        return String(value)
            .replaceAll('&', '&amp;')
            .replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;')
            .replaceAll('"', '&quot;');
    }
}
